import React, { useState, useEffect } from 'react';
import { setChildGoals, getChildGoalProgress } from '../../services/auth';

interface SetGoalsModalProps {
  studentId: string;
  childName: string;
  onClose: () => void;
  onSaved: () => void;
}

export const SetGoalsModal: React.FC<SetGoalsModalProps> = ({
  studentId,
  childName,
  onClose,
  onSaved,
}) => {
  const [questionsPerDay, setQuestionsPerDay] = useState<string>('');
  const [minutesPerDay, setMinutesPerDay] = useState<string>('');
  const [accuracyTarget, setAccuracyTarget] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Load existing goals
    getChildGoalProgress(studentId)
      .then((data) => {
        if (data.goals) {
          if (data.goals.questions_per_day) setQuestionsPerDay(String(data.goals.questions_per_day));
          if (data.goals.minutes_per_day) setMinutesPerDay(String(data.goals.minutes_per_day));
          if (data.goals.accuracy_target) setAccuracyTarget(String(data.goals.accuracy_target));
        }
      })
      .catch(() => {});
  }, [studentId]);

  const parseGoalValue = (val: string): number | null => {
    if (!val || val.trim() === '') return null;
    const num = parseInt(val, 10);
    if (isNaN(num) || num <= 0) return null;
    return num;
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await setChildGoals(studentId, {
        questions_per_day: parseGoalValue(questionsPerDay),
        minutes_per_day: parseGoalValue(minutesPerDay),
        accuracy_target: accuracyTarget && parseFloat(accuracyTarget) > 0 ? parseFloat(accuracyTarget) : null,
      });
      onSaved();
      onClose();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        setError(detail);
      } else if (Array.isArray(detail)) {
        setError(detail.map((d: any) => d.msg || String(d)).join('; '));
      } else {
        setError('Failed to save goals');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleClearGoals = async () => {
    setSaving(true);
    setError('');
    try {
      await setChildGoals(studentId, {
        questions_per_day: null,
        minutes_per_day: null,
        accuracy_target: null,
      });
      setQuestionsPerDay('');
      setMinutesPerDay('');
      setAccuracyTarget('');
      onSaved();
      onClose();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        setError(detail);
      } else {
        setError('Failed to clear goals');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Set Daily Goals for {childName}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          <p className="modal-description">
            Set daily practice goals to keep your child on track. Leave blank to disable a goal.
          </p>

          <div className="form-group">
            <label>Questions per day</label>
            <input
              type="number"
              min="1"
              max="200"
              value={questionsPerDay}
              onChange={(e) => setQuestionsPerDay(e.target.value)}
              placeholder="e.g. 20"
            />
          </div>

          <div className="form-group">
            <label>Minutes per day</label>
            <input
              type="number"
              min="1"
              max="480"
              value={minutesPerDay}
              onChange={(e) => setMinutesPerDay(e.target.value)}
              placeholder="e.g. 30"
            />
          </div>

          <div className="form-group">
            <label>Accuracy target (%)</label>
            <input
              type="number"
              min="0"
              max="100"
              step="5"
              value={accuracyTarget}
              onChange={(e) => setAccuracyTarget(e.target.value)}
              placeholder="e.g. 80"
            />
          </div>

          {error && <p className="error-message">{error}</p>}
        </div>

        <div className="modal-footer">
          <button
            className="danger-btn"
            onClick={handleClearGoals}
            disabled={saving}
            title="Remove all goals"
          >
            Clear Goals
          </button>
          <div className="modal-footer-right">
            <button className="secondary-btn" onClick={onClose}>Cancel</button>
            <button className="primary-btn" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Goals'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
